// @flow
import React, { useState, useCallback, useMemo } from "react";
import { usePdfHighlightActivityList } from "hooks";
import {
    type ActivityDTO,
    ActivityType,
    ItemNoteType,
    type ItemNoteTypeEnum,
} from "data/types";
import Activity from "./Activity";
import FormBarFactory from "components/FormBar/Factory";
import { FormBarView } from "data/types";
import { getLocalizedText } from "data/utils";
import { ActivityAction, type ActivityActionEnum } from "./Actions";
import styles from "./ActivityList.module.css";

const views = [FormBarView.Activity];

type ActionCallback<T> = (actionId: string, payload: T | number) => any;
type ActionFactory<T> = (item: T, index: number) => ActionCallback<T>;

type Props = {
    /** list of activities */
    items: Array<ActivityDTO>,
    /** current user's id */
    userId: number,
    /** whether to show activities details */
    showDetails: boolean,
    /** whether to show history menu
     * @default {boolean} true
     */
    showActions?: boolean,
    /** whether to show notes (comment) entries */
    showNotes: boolean,
    /** whether to show history entries */
    showHistory: boolean,
    /** which version should be selected
     * 0 to show latest
     */
    currentVersion: number,
    /** callback when user chooses an action
     * @param {string} actionId action identifier
     * @param {ActivityDTO | number} payload payload or activity row index
     */
    onAction: ActionCallback<ActivityDTO>,
    /** whether ActivityList is read-only */
    isReadOnly?: boolean,
};

/** Renders a list of activities */
const ActivityList = ({
    items,
    userId,
    showDetails,
    showNotes = true,
    showHistory = true,
    showActions = true,
    currentVersion,
    onAction,
    isReadOnly = false,
    isPrefiltered = false,
}: Props) => {
    const [edit, setEdit] = useState(null);
    const [reply, setReply] = useState(null);
    const [activityType, setActivityType] = useState(null);
    const [commentType, setCommentType] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [customFilter, setCustomFilter] = useState(null);

    const [highlight, handleHighlight] = usePdfHighlightActivityList(
        items,
        userId,
        setEdit,
        onAction,
        isReadOnly
    );

    const handleAction: ActionFactory<ActivityDTO> = useCallback(
        (item: ActivityDTO, index: number) =>
            (actionId: ActivityActionEnum | string, payload: any = index) => {
                switch (actionId) {
                    case ActivityAction.Filter:
                        const itemCommentType: ItemNoteTypeEnum =
                            (item.what === ActivityType.comment &&
                                item.extras?.$type) ||
                            ItemNoteType.Default;
                        setActivityType(
                            commentType !== itemCommentType
                                ? ActivityType.comment
                                : null
                        );
                        return setCommentType(
                            commentType !== itemCommentType
                                ? itemCommentType
                                : null
                        );
                    case ActivityAction.Edit:
                        return setEdit(payload ? item.id : null);
                    case ActivityAction.Reply:
                        return setReply(payload ? item.id : null);
                    case ActivityAction.Highlight:
                        return handleHighlight(
                            highlight !== item.id ? item : null
                        );
                    case ActivityAction.Update:
                        return onAction(actionId, payload).then((success) => {
                            /* istanbul ignore else */
                            if (success) setEdit(null);
                            return success;
                        });
                    case ActivityAction.Delete:
                        return onAction(actionId, payload).then((success) => {
                            /* istanbul ignore else */
                            if (success) {
                                setEdit(null);
                                handleHighlight(null);
                            }
                            return success;
                        });
                    default:
                        return onAction(actionId, payload);
                }
            },
        [commentType, highlight, handleHighlight, onAction]
    );

    const filterType = useCallback(
        (item: ActivityDTO): boolean => {
            if (customFilter != null) {
                return item.extras?.$filterKey === customFilter;
            }
            const isCurrentUser =
                currentUser == null || currentUser === item.userId;
            if (commentType != null)
                return (
                    showNotes &&
                    isCurrentUser &&
                    item.what === ActivityType.comment &&
                    (item.extras?.$type === commentType ||
                        (!item.extras && commentType === ItemNoteType.Default))
                );
            const defaultActivityTypes = [
                ActivityType.comment,
                ActivityType.system,
            ];
            if (defaultActivityTypes.includes(item.what)) {
                return (
                    showNotes &&
                    isCurrentUser &&
                    (activityType == null || item.what === activityType)
                );
            }
            if (activityType != null) {
                if (activityType === ActivityType.history)
                    return isCurrentUser && showHistory;
                return (
                    showHistory && isCurrentUser && item.what === activityType
                );
            }
            return showHistory && isCurrentUser;
        },
        [
            customFilter,
            commentType,
            activityType,
            currentUser,
            showHistory,
            showNotes,
        ]
    );

    if (!showNotes && !showHistory)
        return (
            <div className={styles.wrapper}>
                {getLocalizedText("$activity:list.invalid")}
            </div>
        );
    let lastVersionId = 1;
    const totalVersions = items.reduce((accumulator, item: ActivityDTO) => {
        if (item.what !== ActivityType.comment) {
            lastVersionId = item.id;
            return accumulator + 1;
        }
        return accumulator;
    }, 0);

    const itemsToRender = useMemo(() => items.filter(filterType), [items, filterType]);

    return (
        <>
            <FormBarFactory
                className={styles.formbar}
                views={views}
                commentType={commentType}
                setCommentType={setCommentType}
                activityType={activityType}
                setActivityType={setActivityType}
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                customFilter={customFilter}
                setCustomFilter={setCustomFilter}
                showNotes={showNotes}
                showHistory={showHistory}
                items={items}
                itemsToRender={itemsToRender}
                isPrefiltered={isPrefiltered}
                shouldUseContext={false}
            />
            {itemsToRender.length ? (
                itemsToRender.map((item: ActivityDTO, index: number) => (
                    <Activity
                        key={`a-${item.what}-${item.id}`}
                        activity={item}
                        userId={userId}
                        showDetails={showDetails}
                        isEdit={
                            item.what === ActivityType.comment &&
                            edit === item.id
                        }
                        isReply={
                            item.what === ActivityType.comment &&
                            reply === item.id
                        }
                        isHighlighted={
                            item.what === ActivityType.comment &&
                            highlight === item.id
                        }
                        isSelected={
                            item.what !== ActivityType.comment &&
                            currentVersion === item.id
                        }
                        isLastVersion={
                            item.what !== ActivityType.comment &&
                            lastVersionId === item.id
                        }
                        actionMenu={
                            isReadOnly
                                ? "docactivity_readonly_context"
                                : "docactivity_context"
                        }
                        showActions={totalVersions > 1 && showActions}
                        onAction={handleAction(item, index)}
                        isReadOnly={isReadOnly}
                    />
                ))
            ) : (
                <div className={styles.wrapper}>
                    {getLocalizedText("$activity:list.empty")}
                </div>
            )}
        </>
    );
};
ActivityList.displayName = "ActivityList";
export default ActivityList;
